import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../auth/auth-context';
import { addToWatchlist, removeFromWatchlist, type Watchlist } from '../lib/api';
import { useWatchlistCache, useWatchlists, watchlistErrorMessage } from '../lib/watchlists';

/**
 * A disclosure on the ticker page listing the user's watchlists as
 * checkboxes; checking one adds this ticker to it. Watchlists are a
 * convenience, so if they fail to load the control simply stays hidden.
 */
export function WatchlistToggle({ symbol }: { symbol: string }) {
  const { session } = useAuth();
  const token = session?.access_token ?? '';
  const watchlists = useWatchlists(token);
  const cache = useWatchlistCache(token);
  // The box shows the requested state at once; a failed request reverts it.
  const [pending, setPending] = useState<{ id: number; checked: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!watchlists.isSuccess) return null;
  const lists = watchlists.data.watchlists;
  const contains = (watchlist: Watchlist) =>
    watchlist.tickers.some((ticker) => ticker.symbol === symbol);
  const memberships = lists.filter(contains).length;

  async function toggle(watchlist: Watchlist) {
    const member = contains(watchlist);
    setPending({ id: watchlist.id, checked: !member });
    setError(null);
    try {
      const change = member ? removeFromWatchlist : addToWatchlist;
      cache.replaced(await change(token, watchlist.id, symbol));
    } catch (caught) {
      setError(watchlistErrorMessage(caught, symbol));
    } finally {
      setPending(null);
    }
  }

  return (
    <details className="watchlist-menu">
      <summary className="button button-quiet">
        {memberships === 0
          ? 'Add to watchlist'
          : `In ${memberships} watchlist${memberships === 1 ? '' : 's'}`}
      </summary>
      <div className="watchlist-menu-panel">
        {lists.length === 0 ? (
          <p>You have no watchlists yet.</p>
        ) : (
          <fieldset>
            <legend className="visually-hidden">Watchlists containing {symbol}</legend>
            {lists.map((watchlist) => (
              <label key={watchlist.id}>
                <input
                  type="checkbox"
                  checked={pending?.id === watchlist.id ? pending.checked : contains(watchlist)}
                  disabled={pending !== null}
                  onChange={() => void toggle(watchlist)}
                />
                {watchlist.name}
              </label>
            ))}
          </fieldset>
        )}
        {error ? (
          <p className="form-error" role="alert">
            {error}
          </p>
        ) : null}
        <Link className="text-link" to="/watchlists">
          {lists.length === 0 ? 'Create a watchlist' : 'Manage watchlists'}
        </Link>
      </div>
    </details>
  );
}
